import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, LinearProgress, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ClientAutocomplete from "../../components/clientAutocomplete";
import AnalysisPageTopbar from "../../components/metrics/AnalysisPageTopbar";
import { PAGE_FONT, useMetricsUi } from "../../components/metrics/metricsUi";
import { isMobile } from "../../globalStyles/mobile";
import wsManager from "../../utils/websocketManager";
import { commandCenterService } from "../../utils/services/commandCenterService";
import {
  SIGNALS,
  bannerState,
  commandCenterErrorMessage,
  fmtTime,
  foldedLines,
  laneItems,
  rulesText,
  sectionTiles,
  timeAgo,
} from "../../utils/commandCenterPageDefaults";
import { StatePanel, eyebrowSx } from "../purchase-order-analysis/parts";
import Section from "./Section";
import SummaryTile from "./SummaryTile";

const POLL_MS = 5000;
// Stop polling after ~5 minutes; the WebSocket push still updates the page.
const MAX_POLLS = 60;
const CLOCK_MS = 30000;
const HIGHLIGHT_MS = 2200;
const sectionId = (signal) => `cc-section-${signal}`;

const scrollToId = (id) => {
  const node = document.getElementById(id);
  if (node && typeof node.scrollIntoView === "function") node.scrollIntoView({ behavior: "smooth", block: "start" });
};

const CommandCenterPage = () => {
  const ui = useMetricsUi();
  const { colors, darkMode } = ui;
  const mobile = isMobile();
  const navigate = useNavigate();
  const storedClientId = localStorage.getItem("client_id");
  const [selectedClientData, setSelectedClientData] = useState(null);
  const clientId = selectedClientData?._id || storedClientId;

  const [state, setState] = useState({ snapshot: null, loading: false, error: "" });
  const [tick, setTick] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [refreshNote, setRefreshNote] = useState("");
  const [expandedSections, setExpandedSections] = useState(() => new Set());
  const [highlightedKey, setHighlightedKey] = useState(null);
  const polls = useRef(0);

  const params = useMemo(() => (storedClientId ? {} : { client_id: clientId }), [storedClientId, clientId]);
  const reload = useCallback(() => setTick((value) => value + 1), []);

  useEffect(() => {
    setState({ snapshot: null, loading: false, error: "" });
    setRefreshNote("");
    setExpandedSections(new Set());
    setHighlightedKey(null);
    polls.current = 0;
  }, [clientId]);

  useEffect(() => {
    if (!clientId) return undefined;
    const controller = new AbortController();
    setState((previous) => ({ ...previous, loading: true, error: "" }));
    commandCenterService
      .fetch(params, { signal: controller.signal })
      .then((snapshot) => setState({ snapshot, loading: false, error: "" }))
      .catch((err) => {
        if (!controller.signal.aborted) {
          setState((previous) => ({ ...previous, loading: false, error: commandCenterErrorMessage(err) }));
        }
      });
    return () => controller.abort();
  }, [clientId, params, tick]);

  // While a build runs, poll until it lands (the WebSocket push usually wins).
  const snapshot = state.snapshot;
  const building = snapshot?.status === "building" || Boolean(snapshot?.refreshing);
  useEffect(() => {
    if (!building || state.loading) return undefined;
    if (polls.current >= MAX_POLLS) return undefined;
    const timer = setTimeout(() => {
      polls.current += 1;
      reload();
    }, POLL_MS);
    return () => clearTimeout(timer);
  }, [building, state.loading, reload]);

  useEffect(() => {
    const user = localStorage.getItem("user");
    try {
      if (user) wsManager.connect(JSON.parse(user)._id);
    } catch (err) {
      // No usable session user: polling still refreshes the page.
    }
    return wsManager.on("commandCenterUpdated", (message) => {
      if (message?.client_id && String(message.client_id) === String(clientId)) {
        polls.current = 0;
        reload();
      }
    });
  }, [clientId, reload]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), CLOCK_MS);
    return () => clearInterval(timer);
  }, []);

  const onRefresh = async () => {
    setRefreshNote("");
    try {
      const result = await commandCenterService.refresh(params);
      if (result?.reason === "cooldown") {
        setRefreshNote("Checked less than 5 minutes ago — results are current.");
        return;
      }
      polls.current = 0;
      reload();
    } catch (err) {
      setRefreshNote(commandCenterErrorMessage(err));
    }
  };

  const onOpenLink = (link) =>
    navigate(link.path, {
      state: link.state ? { ...link.state, client: storedClientId ? null : selectedClientData } : null,
    });

  const ready = snapshot && snapshot.status !== "building" && Array.isArray(snapshot.items);
  const items = useMemo(() => (ready ? snapshot.items : []), [ready, snapshot]);
  const relatedTitles = useMemo(() => new Map(items.map((item) => [item.key, item.title])), [items]);
  const staleFeeds = ready ? (snapshot.freshness?.feeds || []).filter((feed) => feed.stale) : [];
  const folded = ready ? foldedLines(snapshot) : [];
  const verdict = ready ? bannerState(snapshot) : null;
  const verdictColor = verdict?.tone === "alert" ? ui.danger : verdict?.tone === "watch" || staleFeeds.length ? ui.warning : ui.positive;

  useEffect(() => {
    if (!highlightedKey) return undefined;
    const timer = setTimeout(() => setHighlightedKey(null), HIGHLIGHT_MS);
    return () => clearTimeout(timer);
  }, [highlightedKey]);

  // A linked item may sit past its section's first five: open the section,
  // then scroll to the item once it has rendered, and flash it.
  const onJumpTo = (key) => {
    const target = items.find((item) => item.key === key);
    if (!target) return;
    setExpandedSections((current) => new Set([...current, target.signal]));
    setHighlightedKey(key);
    setTimeout(() => scrollToId(`cc-${key}`), 0);
  };

  return (
    <Box sx={{ minHeight: "100%", backgroundColor: darkMode ? colors.bgPrimary : "#F5F6F8", fontFamily: PAGE_FONT, textAlign: "left" }}>
      {state.loading && <LinearProgress sx={{ position: "sticky", top: 0, zIndex: 10, height: 3 }} />}
      <Box sx={{ maxWidth: 1480, mx: "auto", padding: mobile ? "14px 12px 40px" : "22px 28px 56px" }}>
        <AnalysisPageTopbar
          badge="CC"
          title="Command Center"
          mb={2}
          clientName={selectedClientData?.business_name || selectedClientData?.name}
          right={
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
              {ready ? (
                <Typography sx={{ fontSize: 12, color: colors.textSecondary, textAlign: "right" }}>
                  {refreshNote || (
                    <>
                      Checked <strong>{timeAgo(snapshot.computed_at, now)}</strong>
                      {snapshot.data_as_of?.orders ? ` · data through ${fmtTime(snapshot.data_as_of.orders)}` : ""}
                      {snapshot.refreshing ? " · checking again…" : ""}
                    </>
                  )}
                </Typography>
              ) : null}
              {clientId ? (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={onRefresh}
                  disabled={building}
                  sx={{ textTransform: "none" }}
                >
                  {building ? "Checking…" : "Check now"}
                </Button>
              ) : null}
              {!storedClientId ? (
                <ClientAutocomplete width={220} selectedClient={selectedClientData} onClientChange={setSelectedClientData} />
              ) : null}
            </Box>
          }
        />

        {!clientId && <StatePanel ui={ui} eyebrow="Client required" title="Select a client to see what needs attention." />}
        {clientId && state.error && !ready && (
          <StatePanel ui={ui} tone="error" eyebrow="Could not load" title={state.error} />
        )}
        {clientId && !state.error && !ready && (
          <StatePanel
            ui={ui}
            eyebrow="Preparing"
            title="Checking your stock, purchase orders and customer orders…"
            body="The first check for a client takes up to a minute. After that this page opens instantly and is kept up to date in the background."
          />
        )}

        {ready && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
              {verdict.tone === "alert" ? (
                <Box
                  aria-hidden
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: verdictColor,
                    animation: "ccPulse 1.8s ease-out infinite",
                    "@keyframes ccPulse": {
                      "0%": { boxShadow: `0 0 0 0 ${verdictColor}` },
                      "70%": { boxShadow: "0 0 0 8px rgba(0,0,0,0)" },
                      "100%": { boxShadow: "0 0 0 0 rgba(0,0,0,0)" },
                    },
                    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                  }}
                />
              ) : (
                <Box aria-hidden sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: verdictColor }} />
              )}
              <Typography component="p" sx={{ fontSize: 18, fontWeight: 700, color: colors.textPrimary }}>
                {verdict.title}
              </Typography>
            </Box>

            {staleFeeds.length || snapshot.status === "partial" ? (
              <Box sx={{ display: "flex", gap: 1, px: 1.75, py: 1.1, borderRadius: "10px", backgroundColor: ui.warningBg }}>
                <WarningAmberOutlinedIcon sx={{ fontSize: 18, color: ui.warning, mt: "1px" }} />
                <Box>
                  {staleFeeds.map((feed) => (
                    <Typography key={feed.feed} sx={{ fontSize: 13, color: ui.warning, fontWeight: 600 }}>
                      {feed.label} {feed.last_update ? `last updated ${timeAgo(feed.last_update, now)}` : `not updated in ${feed.within_days} days`} —
                      items below may be out of date.
                    </Typography>
                  ))}
                  {snapshot.status === "partial" ? (
                    <Typography sx={{ fontSize: 13, color: ui.warning, fontWeight: 600 }}>
                      Could not check {(snapshot.build_errors || []).map((row) => row.warehouse_name || row.warehouse_id).join(", ")} this time — its
                      items may be missing.
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            ) : null}

            <Box sx={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "repeat(3, minmax(0, 1fr))", gap: 2 }}>
              {sectionTiles(snapshot).map((tile) => (
                <SummaryTile key={tile.key} ui={ui} tile={tile} onClick={() => scrollToId(sectionId(tile.key))} />
              ))}
            </Box>

            {SIGNALS.map((signal) => (
              <Section
                key={signal.key}
                id={sectionId(signal.key)}
                ui={ui}
                signal={signal.key}
                label={signal.label}
                items={laneItems(items, signal.key)}
                folded={folded.filter((line) => line.lane === signal.key)}
                snapshot={snapshot}
                expanded={expandedSections.has(signal.key)}
                onExpand={() => setExpandedSections((current) => new Set([...current, signal.key]))}
                highlightedKey={highlightedKey}
                relatedTitles={relatedTitles}
                onOpenLink={onOpenLink}
                onJumpTo={onJumpTo}
                onNavigate={(path) => navigate(path)}
              />
            ))}

            {snapshot.resolved?.length ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography sx={{ ...eyebrowSx(colors), color: ui.positive }}>Resolved in the last day</Typography>
                {snapshot.resolved.map((row) => (
                  <Box
                    key={row.key}
                    component="span"
                    sx={{ fontSize: 12, px: 1, py: 0.25, borderRadius: "10px", color: ui.positive, backgroundColor: ui.positiveBg }}
                  >
                    ✓ {row.title}
                  </Box>
                ))}
              </Box>
            ) : null}

            <Typography sx={{ fontSize: 11.5, color: colors.textTertiary, lineHeight: 1.6 }}>{rulesText(snapshot)}</Typography>
          </Box>
        )}
      </Box>

    </Box>
  );
};

export default CommandCenterPage;
